import { useState, useEffect, useRef } from "react";
import { getRequest } from "../../../utils/axios";
import Loader from "../../atoms/Loader";
import generatePDF, { Resolution, Margin } from "react-to-pdf";
import { NotificationManager } from "react-notifications";
import "./styles.scss";

const imageBaseUrl = `https://dynamicproductstorage.blob.core.windows.net/gallery/`;

export default function CertificateViewer({ pageId, runType, onClose, textColor = '#ffffff' }) {
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const modalRef = useRef(null);

  const options = {
    method: "save",
    resolution: Resolution.MEDIUM,
    page: {
      margin: Margin.MEDIUM,
    },
    overrides: {
      pdf: { compress: true },
      canvas: { useCORS: true },
    },
  };

  useEffect(() => {
    if (pageId) {
      fetchCertificates([pageId]);
    }
  }, [pageId]);

  const fetchCertificates = async (pageIds) => {
    setLoading(true);
    try {
      const certsArray = await Promise.all(
        pageIds.map(async (id) => {
          const res = await getRequest(
            `EventCertificate/GetEventCertificateByEventId/${id}`
          );
          return { pageId: id, data: res.data };
        })
      );

      const map = {};

      certsArray.forEach(({ pageId, data }) => {
        if (!data) return;

        if (Array.isArray(data) && data.length > 0) {
          data.forEach((c) => {
            const runKey = c.runType
              ? `${pageId}-${c.runType.toLowerCase()}`
              : null;

            const url = `${c.galleryFilePath.replace(/\\/g, "/")}${c.galleryFileName}`;
            if (runKey) {
              map[runKey] = {
                url,
                certificateCode: c.certificateCode || null,
              };
            }
          });
        }
      });

      // 🔥 Automatically open demo preview
      const certKey = `${pageId}-${runType?.toLowerCase()}`;
      const meta = map[certKey];

      if (meta) {
        openDemoPreview(meta);
      } else {
        NotificationManager.error("Certificate not found for this runType.");
        if (typeof onClose === "function") onClose();
      }
    } catch (error) {
      console.error("Error fetching certificates:", error);
    } finally {
      setLoading(false);
    }
  };

  const openDemoPreview = (meta) => {
    setPreviewLoading(true);

    if(runType === "fun") {
        setSelectedCertificate({
        certificateUrl: `${imageBaseUrl}${meta.url}`,
        participantName: "XXXX XXXXX",
        ticketName: "XXXXX",
        });
    };
    if(runType === "bib") {
        setSelectedCertificate({
        certificateUrl: `${imageBaseUrl}${meta.url}`,
        participantName: "XXXX XXXXX",
        ticketName: "XXXXX",
        certificateCode: meta.certificateCode || null,
        completionTime: "00:00:00",
        bibNo: "XXXXX",
        ageCategory: "00-00",
        ageRank: "0",
        genderRank: "0",
        });
    };
 };

  const handlePdfDownload = async () => {
    setIsPdfGenerating(true);
    try {
      options.filename = `Demo Certificate.pdf`;
      await generatePDF(getTargetElement, options);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const getTargetElement = () =>
    document.getElementById("content-id");

  return (
    <>
      {loading && <Loader loading />}

      {selectedCertificate && (
        <div className="preview-modal">
          {(previewLoading || isPdfGenerating) && (
            <div className="overlay-loader">
              <Loader loading />
            </div>
          )}

          <div className="preview-container">
            <div className="popup-buttons">
              <a
                onClick={handlePdfDownload}
                className={`popup-button2 ${isPdfGenerating ? "disabled" : ""}`}
              >
                ⭳
              </a>

              <a
                onClick={() => {
                  setSelectedCertificate(null);
                  if (typeof onClose === "function") onClose();
                }}
                className="popup-button"
              >
                ✖
              </a>
            </div>

            <div
              className={`v-certificate-wrapper ${
                previewLoading ? "hidden" : ""
              }`}
              id="content-id"
            >
              <img
                width={1241 / 2}
                height={1754 / 2}
                src={selectedCertificate.certificateUrl}
                onLoad={() => setPreviewLoading(false)}
                onError={() => {
                  setPreviewLoading(false);
                  alert("Failed to load image.");
                }}
              />

              <p className="name" style={{ color: textColor }}>{selectedCertificate.participantName}</p>
              <p className="objective" style={{ color: textColor }}>
                {selectedCertificate.ticketName}
              </p>

              {selectedCertificate.certificateCode && (
                <p className="certificate-code" style={{ color: textColor }}>
                  {selectedCertificate.certificateCode}
                </p>
              )}

              <p className="completion-time" style={{ color: textColor }}>
                {selectedCertificate.completionTime}
              </p>

              {selectedCertificate.bibNo && (
                <p className="bib-no" style={{ color: textColor }}>
                  {selectedCertificate.bibNo}
                </p>
              )}

              <p className="age-category" style={{ color: textColor }}>
                {selectedCertificate.ageCategory}
              </p>

              <p className="age-rank" style={{ color: textColor }}>
                {selectedCertificate.ageRank}
              </p>

              <p className="gender-rank" style={{ color: textColor }}>
                {selectedCertificate.genderRank}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}